import { useState } from "react";
import useSWR from "swr";
import { api, fmtEur, fmtNum, WEEKDAYS_SK } from "@/lib/app";
import { FloppyDisk, Trash, FolderOpen, X, ArrowsHorizontal, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

function Delta({ value, fmt = fmtNum, suffix = "", invert = false }) {
  if (value === null || value === undefined) return <span>—</span>;
  // invert=true means "lower is better" (e.g. cost, person-hours)
  const positive = invert ? value < 0 : value > 0;
  const negative = invert ? value > 0 : value < 0;
  const tone = positive ? "text-[var(--success)]" : negative ? "text-[var(--critical)]" : "text-[var(--text-muted)]";
  return (
    <span className={`tabular ${tone}`}>
      {value > 0 ? "+" : ""}{fmt(value)}{suffix}
    </span>
  );
}

export default function Scenarios({ storeId, period, forecastFrom, forecastTo, hourlyWage, mods, onLoad }) {
  const { data: list = [], mutate } = useSWR(`/stores/${storeId}/scenarios`, fetcher);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [compare, setCompare] = useState([]); // ids of scenarios selected to compare

  const canSave = name.trim().length > 0 && period?.from && forecastFrom && forecastTo;

  const save = async () => {
    if (!canSave) {
      toast.error("Zadaj meno scenára a skontrolujte obdobia.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/scenarios", {
        store_id: storeId,
        name: name.trim(),
        description: desc.trim(),
        period_from: period.from,
        period_to: period.to,
        forecast_from: forecastFrom,
        forecast_to: forecastTo,
        hourly_wage: hourlyWage,
        modifications: mods,
      });
      toast.success("Scenár uložený");
      setName("");
      setDesc("");
      mutate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nepodarilo sa uložiť scenár.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Naozaj zmazať scenár?")) return;
    try {
      await api.delete(`/scenarios/${id}`);
      setCompare((c) => c.filter((x) => x !== id));
      mutate();
      toast.success("Zmazané");
    } catch {
      toast.error("Nepodarilo sa zmazať.");
    }
  };

  const toggleCompare = (id) => {
    setCompare((c) => {
      if (c.includes(id)) return c.filter((x) => x !== id);
      if (c.length >= 3) {
        toast.info("Maximálne 3 scenáre naraz.");
        return c;
      }
      return [...c, id];
    });
  };

  const compareItems = compare
    .map((id) => list.find((s) => s.id === id))
    .filter(Boolean);

  // Build a baseline pick (first selected) for delta vs others
  const baseline = compareItems[0];

  return (
    <div className="space-y-4">
      {/* Save form */}
      <div className="card-flat p-5">
        <h3 className="font-display text-base font-semibold mb-1 inline-flex items-center gap-2">
          <FloppyDisk size={18} weight="duotone" className="text-[var(--brand)]" /> Uložiť scenár
        </h3>
        <p className="text-xs text-[var(--text-secondary)] mb-3">
          Ulož aktuálne nastavenie (obdobia, mzda, zmeny v rozvrhu) ako pomenovaný scenár. Neskôr ho jedným klikom načítaš späť alebo porovnáš s iným.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2">
          <input
            type="text"
            placeholder="napr. „Piatkový tím +1“"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="text-sm"
            data-testid="scenario-name-input"
          />
          <input
            type="text"
            placeholder="krátky popis (voliteľné)"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            className="text-sm"
            data-testid="scenario-desc-input"
          />
          <button
            onClick={save}
            disabled={!canSave || saving}
            className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="scenario-save-button"
          >
            <FloppyDisk size={14} /> {saving ? "Ukladám…" : "Uložiť"}
          </button>
        </div>
        <p className="text-[11px] text-[var(--text-muted)] mt-2">
          Aktuálne: {mods.length} úprav · forecast {forecastFrom || "?"} → {forecastTo || "?"} · mzda {fmtEur(hourlyWage)}/h
        </p>
      </div>

      {/* List */}
      <div className="card-flat p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-display text-base font-semibold inline-flex items-center gap-2">
            <FolderOpen size={18} weight="duotone" className="text-[var(--brand)]" />
            Uložené scenáre <span className="text-xs text-[var(--text-muted)] font-normal">({list.length})</span>
          </h3>
          {compare.length > 0 && (
            <button
              onClick={() => setCompare([])}
              className="btn-ghost text-xs inline-flex items-center gap-1"
              data-testid="scenarios-clear-compare"
            >
              <X size={12} /> Zrušiť výber ({compare.length})
            </button>
          )}
        </div>

        {list.length === 0 && (
          <p className="text-xs text-[var(--text-muted)] italic">
            Zatiaľ žiadne uložené scenáre. Nastavte úpravy vyššie a kliknite „Uložiť“.
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {list.map((s) => {
            const isSelected = compare.includes(s.id);
            const su = s.summary || {};
            const phDelta = su.person_hours_delta || 0;
            const costDelta = su.cost_delta || 0;
            return (
              <div
                key={s.id}
                className={`rounded-lg p-3 border transition ${isSelected ? "border-[var(--brand)] bg-[var(--brand)]/5" : "border-[var(--border-default)] bg-white"}`}
                data-testid={`scenario-card-${s.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="min-w-0 flex-1">
                    <h4 className="font-display text-sm font-semibold truncate">{s.name}</h4>
                    {s.description && (
                      <p className="text-[11px] text-[var(--text-muted)] truncate">{s.description}</p>
                    )}
                  </div>
                  <button
                    onClick={() => remove(s.id)}
                    className="text-[var(--text-muted)] hover:text-[var(--critical)] shrink-0"
                    title="Zmazať"
                    data-testid={`scenario-${s.id}-remove`}
                  >
                    <Trash size={12} />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                  <div>
                    <p className="label-eyebrow">Δ os.hodín</p>
                    <p className="font-semibold"><Delta value={phDelta} fmt={(v) => fmtNum(v, 1)} suffix=" h" invert /></p>
                  </div>
                  <div>
                    <p className="label-eyebrow">Δ náklady</p>
                    <p className="font-semibold"><Delta value={costDelta} fmt={fmtEur} invert /></p>
                  </div>
                </div>

                <p className="text-[11px] text-[var(--text-muted)] mt-2">
                  {(s.modifications || []).length} úprav · forecast {s.forecast_from} → {s.forecast_to}
                </p>

                <div className="flex items-center gap-2 mt-3">
                  <button
                    onClick={() => onLoad(s)}
                    className="btn-ghost text-xs inline-flex items-center gap-1 flex-1 justify-center"
                    data-testid={`scenario-${s.id}-load`}
                  >
                    <FolderOpen size={12} /> Načítať
                  </button>
                  <button
                    onClick={() => toggleCompare(s.id)}
                    className={`text-xs inline-flex items-center gap-1 px-2 py-1.5 rounded transition ${isSelected ? "bg-[var(--brand)] text-white" : "bg-[var(--bg-page)] hover:bg-[var(--border-default)]"}`}
                    data-testid={`scenario-${s.id}-compare`}
                  >
                    {isSelected ? <><Check size={12} /> Vybraný</> : <><ArrowsHorizontal size={12} /> Porovnať</>}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Side-by-side comparison */}
      {compareItems.length >= 2 && (
        <div className="card-flat p-5">
          <h3 className="font-display text-base font-semibold mb-1 inline-flex items-center gap-2">
            <ArrowsHorizontal size={18} weight="duotone" className="text-[var(--brand)]" />
            Porovnanie scenárov
          </h3>
          <p className="text-xs text-[var(--text-secondary)] mb-4">
            Bázový scenár je <strong>{baseline.name}</strong> (prvý vybraný). Δ stĺpce ukazujú rozdiel oproti nemu — negatívne hodnoty pri nákladoch / osobohodinách znamenajú úsporu.
          </p>

          <div className="overflow-x-auto scroll-area">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left">
                  <th className="pb-2 font-medium text-[var(--text-secondary)]">Metrika</th>
                  {compareItems.map((s) => (
                    <th key={s.id} className="pb-2 font-display text-sm font-semibold pr-3" data-testid={`compare-col-${s.id}`}>
                      {s.name}
                      {s.id === baseline.id && <span className="ml-1 text-[10px] uppercase tracking-wider text-[var(--brand)]">báza</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="tabular">
                <tr className="border-t border-[var(--border-default)]">
                  <td className="py-2 text-[var(--text-secondary)]">Pred. tržba (báza)</td>
                  {compareItems.map((s) => (
                    <td key={s.id} className="py-2 pr-3">{fmtEur(s.summary?.base_pred_revenue)}</td>
                  ))}
                </tr>
                <tr className="border-t border-[var(--border-default)]">
                  <td className="py-2 text-[var(--text-secondary)]">Os.hodín (po úpravách)</td>
                  {compareItems.map((s) => (
                    <td key={s.id} className="py-2 pr-3">
                      {fmtNum(s.summary?.modified_person_hours, 1)} h
                      {s.id !== baseline.id && (
                        <span className="ml-2 text-xs">
                          (<Delta
                            value={(s.summary?.modified_person_hours || 0) - (baseline.summary?.modified_person_hours || 0)}
                            fmt={(v) => fmtNum(v, 1)}
                            suffix=" h"
                            invert
                          />)
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
                <tr className="border-t border-[var(--border-default)]">
                  <td className="py-2 text-[var(--text-secondary)]">Mzdové náklady</td>
                  {compareItems.map((s) => (
                    <td key={s.id} className="py-2 pr-3">
                      {fmtEur(s.summary?.modified_cost)}
                      {s.id !== baseline.id && (
                        <span className="ml-2 text-xs">
                          (<Delta
                            value={(s.summary?.modified_cost || 0) - (baseline.summary?.modified_cost || 0)}
                            fmt={fmtEur}
                            invert
                          />)
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
                <tr className="border-t border-[var(--border-default)]">
                  <td className="py-2 text-[var(--text-secondary)]">Δ os.hod oproti forecastu</td>
                  {compareItems.map((s) => (
                    <td key={s.id} className="py-2 pr-3">
                      <Delta value={s.summary?.person_hours_delta} fmt={(v) => fmtNum(v, 1)} suffix=" h" invert />
                    </td>
                  ))}
                </tr>
                <tr className="border-t border-[var(--border-default)]">
                  <td className="py-2 text-[var(--text-secondary)]">Δ náklady oproti forecastu</td>
                  {compareItems.map((s) => (
                    <td key={s.id} className="py-2 pr-3">
                      <Delta value={s.summary?.cost_delta} fmt={fmtEur} invert />
                    </td>
                  ))}
                </tr>
                <tr className="border-t border-[var(--border-default)]">
                  <td className="py-2 text-[var(--text-secondary)]">Ovplyvnené sloty</td>
                  {compareItems.map((s) => (
                    <td key={s.id} className="py-2 pr-3">{s.summary?.impacted_slots ?? 0}</td>
                  ))}
                </tr>
                <tr className="border-t border-[var(--border-default)]">
                  <td className="py-2 text-[var(--text-secondary)]">Mzda / h</td>
                  {compareItems.map((s) => (
                    <td key={s.id} className="py-2 pr-3">{fmtEur(s.hourly_wage)}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Modifications list per scenario */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mt-4">
            {compareItems.map((s) => (
              <div key={s.id} className="bg-[var(--bg-page)] rounded p-3">
                <p className="label-eyebrow mb-2">{s.name} — úpravy</p>
                {(s.modifications || []).length === 0 ? (
                  <p className="text-xs text-[var(--text-muted)] italic">Bez úprav (čistý forecast)</p>
                ) : (
                  <ul className="text-xs space-y-1">
                    {(s.modifications || []).map((m, i) => (
                      <li key={i} className="tabular">
                        {WEEKDAYS_SK[m.weekday]} {m.hour_from}-{m.hour_to}h:{" "}
                        <span className={m.delta_employees > 0 ? "text-[var(--critical)] font-semibold" : "text-[var(--success)] font-semibold"}>
                          {m.delta_employees > 0 ? "+" : ""}{m.delta_employees} os.
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
